import { calculateProgressPercentage } from "../../utils/DateUtils/DateUtils";
import { realignSequenceOnGantt } from "../../utils/ScheduleActivityUtils/ScheduleActivityUtils";
import {
  ADD_SCHEDULE_ACTIVITY_SUCCESS,
  ADD_SCHEDULE_ACTIVITY_ERROR,
  ADD_SCHEDULE_ACTIVITY_REQUEST,
  GET_SCHEDULE_ACTIVITIES_ERROR,
  GET_SCHEDULE_ACTIVITIES_REQUEST,
  GET_SCHEDULE_ACTIVITIES_SUCCESS,
  UPDATE_SCHEDULE_ACTIVITIES_REQUEST,
  UPDATE_SCHEDULE_ACTIVITIES_SUCCESS,
  UPDATE_SCHEDULE_ACTIVITIES_ERROR,
  UPDATE_SCHEDULE_ACTIVITIES_WITH_USAGE_SUCCESS,
  UPDATE_DETAIL_ACTIVITY_REQUEST,
  UPDATE_DETAIL_ACTIVITY_SUCCESS,
  UPDATE_DETAIL_ACTIVITY_ERROR,
  CLEAR_SCHEDULE_ACTIVITIES,
  GET_REGULARS_REQUEST,
  GET_REGULARS_SUCCESS,
  GET_REGULARS_ERROR,
  GET_REGULARS_CHUNK,
  SET_REGULARS_SCOPE,
  GET_REGULARS_FROM_CACHE,
  UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_REQUEST,
  UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_SUCCESS,
  UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_ERROR,
  UPDATE_REGULAR,
  DELETE_SCHEDULE_ACTIVITY,
  DELETE_SCHEDULE_ACTIVITIES,
  UPLOAD_MPP_REQUEST,
  UPLOAD_MPP_SUCCESS,
  UPLOAD_MPP_ERROR,
  UPDATE_SCHEDULE_ACTIVITIES_BY_IDS,
  RESET_BASELINE_ON_ACTIVITY,
  RESET_BASELINE_ON_SUMMARY,
  ADD_SUBTASK_SUCCESS,
  ADD_SUMMARY_SUCCESS,
  APPLY_CONFORM_PROGRESS,
  GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_SUCCESS,
  GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_ERROR,
  GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_NAMES_REQUEST,
  GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_NAMES_SUCCESS,
  GET_RESOURCE_TASKS_CUSTOM_COLUMN_NAMES_ERROR,
  GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_REQUEST,
  ADD_SCHEDULE_ACTIVITIES,
  UPDATE_SCHEDULE_ACTIVITY_FINISH_DATE_WITH_DURATION_SUCCESS,
  UPDATE_SCHEDULE_ACTIVITY_CUSTOM_COLUMN,
  UPDATE_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_VALUE,
  SHOW_MASK_ON_GANTT,
  UPDATE_FINISH_AND_START,
  PROPAGATE_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_VALUE,
  UPDATE_BATCH_OF_REGULARS,
} from "../types";
import produce from "immer";
import { saRowInWindow } from "../../utils/periodWindow/periodWindow";

const initialState = {
  item: {},
  items: [],
  regulars: [],
  // Delta-band period cache: regularsPool holds EVERY loaded row (deduped by SA id) across
  // the covered date range; regulars is the pool filtered by the selected display window.
  // Narrowing the period is a pure local re-window (zero network); widening only fetches the
  // missing band(s). Coverage carries the filters key — filters change => full reload.
  regularsPool: [],
  regularsCoverage: null,
  regularsWindow: null,
  // Per-session cache of look-ahead rows keyed by period+filters, so switching back to an
  // already-loaded period serves from memory instead of refetching. regularsCacheKey is the
  // key currently shown.
  regularsCache: {},
  regularsCacheKey: null,
  customColumns: [],
  customColumnNames: [],
  showMaskOnGantt: false,
  isFetching: false,
  isFetchingRegulars: false,
  isUpdating: false,
  isFetchingColumns: false,
  isFetchingColumnNames: false,
  error: "",
};
// Any structural mutation of look-ahead rows must invalidate the per-session period
// cache, otherwise navigating away and back would serve a stale snapshot. Listed
// centrally (and applied by the wrapper below) so add / delete / edit / quick-assign /
// smart-assign / batch-assign can never leave a stale cached period. Custom-column/PK
// edits are intentionally excluded: they live in the separate `customColumns` slice and
// are merged onto rows at render time, so the cached `regulars` stay correct.
const REGULARS_CACHE_INVALIDATING_TYPES = new Set([
  ADD_SCHEDULE_ACTIVITY_SUCCESS,
  ADD_SCHEDULE_ACTIVITIES,
  ADD_SUBTASK_SUCCESS,
  ADD_SUMMARY_SUCCESS,
  DELETE_SCHEDULE_ACTIVITY,
  DELETE_SCHEDULE_ACTIVITIES,
  UPDATE_REGULAR,
  UPDATE_BATCH_OF_REGULARS,
  UPDATE_SCHEDULE_ACTIVITIES_BY_IDS,
  UPDATE_SCHEDULE_ACTIVITIES_SUCCESS,
  UPDATE_SCHEDULE_ACTIVITIES_WITH_USAGE_SUCCESS,
  UPDATE_DETAIL_ACTIVITY_SUCCESS,
  UPDATE_FINISH_AND_START,
  UPDATE_SCHEDULE_ACTIVITY_FINISH_DATE_WITH_DURATION_SUCCESS,
  RESET_BASELINE_ON_ACTIVITY,
  RESET_BASELINE_ON_SUMMARY,
  APPLY_CONFORM_PROGRESS,
  UPLOAD_MPP_SUCCESS,
]);

// Rows from the chunked/full look-ahead load may embed their SA custom-column values
// (`row.customColumns`, keyed `column_<columnNameId>` — same shape as the whole-interval
// endpoint's entries). Upsert them into state.customColumns so the grid paints values
// together with the rows instead of waiting for the separate custom-columns request.
function upsertEmbeddedSACustomColumns(draftState, rows) {
  if (!Array.isArray(rows)) return;
  const withCC = rows.filter((r) => r && r.customColumns);
  if (!withCC.length) return;
  const indexById = new Map(
    draftState.customColumns.map((c, i) => [c.id, i])
  );
  withCC.forEach((r) => {
    const saId = r.scheduleActivity.id;
    const existingIndex = indexById.get(saId);
    if (existingIndex != null) {
      draftState.customColumns[existingIndex] = {
        ...draftState.customColumns[existingIndex],
        ...r.customColumns,
      };
    } else {
      indexById.set(saId, draftState.customColumns.length);
      draftState.customColumns.push({ id: saId, ...r.customColumns });
    }
  });
}

// eslint-disable-next-line
/**
 * Rows the backend changed alongside a creation — the project finish milestone gaining
 * the new row as a predecessor — replace their stale copy so the link survives the next
 * dependency diff.
 */
function replaceItemsById(items, updatedItems) {
  if (!updatedItems?.length) return items;
  const updatedById = new Map(updatedItems.map((item) => [item.id, item]));
  return items.map((item) => updatedById.get(item.id) || item);
}

function baseScheduleActivityReducer(state = initialState, { type, payload }) {
  switch (type) {
    case ADD_SCHEDULE_ACTIVITY_REQUEST:
      return produce(state, (draftState) => {
        draftState.error = "";
      });
    case ADD_SCHEDULE_ACTIVITY_SUCCESS:
      const payloadData = payload.scheduleActivity;
      const itemToAdd =
        "mappingTag" in payloadData
          ? {
            ...payloadData.scheduleActivity,
            mappingTag: payloadData.mappingTag,
          }
          : payloadData;
      return produce(state, (draftState) => {
        draftState.items = replaceItemsById(
          [...draftState.items, itemToAdd],
          payload.reorderData?.updatedItems
        );
        if (payload.reorderData) {
          realignSequenceOnGantt(
            payload.reorderData.taskStore,
            payload.reorderData.scenarioId
          );
        }
      });
    case ADD_SCHEDULE_ACTIVITY_ERROR:
      return produce(state, (draftState) => {
        draftState.error = payload;
      });
    case ADD_SCHEDULE_ACTIVITIES:
      return produce(state, (draftState) => {
        const newItems = draftState.items.slice();
        newItems.push(...payload.created);
        draftState.items = replaceItemsById(newItems, payload.updated);
      });
    case UPDATE_DETAIL_ACTIVITY_REQUEST:
      return {
        ...state,
        isUpdating: true,
        error: "",
      };
    case UPDATE_DETAIL_ACTIVITY_SUCCESS:
      return {
        ...state,
        items: state.items.map((item) => {
          if (item.id === payload.id) {
            return { ...item, ...payload };
          }
          return item;
        }),
        isUpdating: false,
      };
    case UPDATE_DETAIL_ACTIVITY_ERROR:
      return {
        ...state,
        isUpdating: false,
        error: payload,
      };
    case UPDATE_SCHEDULE_ACTIVITIES_REQUEST:
      return produce(state, (draftState) => {
        draftState.error = "";
        draftState.isUpdating = true;
      });
    case UPDATE_SCHEDULE_ACTIVITIES_SUCCESS:
      return produce(state, (draftState) => {
        draftState.items = payload;
        draftState.isUpdating = false;
      });
    case UPDATE_SCHEDULE_ACTIVITIES_ERROR:
      return produce(state, (draftState) => {
        draftState.error = payload;
        draftState.isUpdating = false;
      });
    case UPDATE_SCHEDULE_ACTIVITIES_WITH_USAGE_SUCCESS:
      // Handle server response from "Update usages" - replace items with recalculated schedule
      return produce(state, (draftState) => {
        draftState.items = payload;
        draftState.isUpdating = false;
      });
    case UPLOAD_MPP_REQUEST:
      return produce(state, (draftState) => {
        draftState.isFetching = true;
      });
    case UPLOAD_MPP_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isFetching = false;
        draftState.items = payload;
      });
    case UPLOAD_MPP_ERROR:
      return produce(state, (draftState) => {
        draftState.isFetching = false;
        draftState.error = payload;
      });
    case UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_REQUEST:
      return {
        ...state,
        isUpdating: true,
        error: "",
      };
    case UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_SUCCESS:
      return {
        ...state,
        isUpdating: false,
      };
    case UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_ERROR:
      return {
        ...state,
        isUpdating: false,
        error: payload,
      };
    case GET_SCHEDULE_ACTIVITIES_REQUEST:
      return produce(state, (draftState) => {
        draftState.isFetching = true;
        draftState.items = [];
        draftState.regulars = [];
        draftState.error = "";
      });
    case GET_SCHEDULE_ACTIVITIES_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isFetching = false;
        draftState.showMaskOnGantt = false;
        const newItems = payload.map(
          ({ scheduleActivity, hasConflicts, ...rest }) => ({
            ...scheduleActivity,
            globalCostAtCompletion: rest.globalCostAtCompletion || 0,
            laborCostAtCompletion: rest.laborCostAtCompletion || 0,
            plannedLaborCost: rest.plannedLaborCost || 0,
            plannedGlobalCost: rest.plannedGlobalCost || 0,
            laborCostActuals: rest.laborCostActuals || 0,
            laborCostRemaining: rest.laborCostRemaining || 0,
            globalCostActuals: rest.globalCostActuals || 0,
            globalCostRemaining: rest.globalCostRemaining || 0,
            spectrum: rest.spectrum || null,
            mappingTag: rest.mappingTag || null,
            // Only when the backend sent it: isTaggableRecord falls back to the row
            // flags for payloads older than tagLevel, and a materialized null would
            // silently disable the whole Tag column against such a backend.
            ...("tagLevel" in rest ? { tagLevel: rest.tagLevel } : {}),
            headScopeNumber: rest.headScopeNumber ?? null,
            headScopeLabel:
              rest.headScopeNumber > 0 ? `#${rest.headScopeNumber}` : "",
            hasConflicts,
          })
        );
        draftState.items = newItems;
      });
    case GET_SCHEDULE_ACTIVITIES_ERROR:
      return produce(state, (draftState) => {
        draftState.isFetching = false;
        draftState.error = payload;
      });
    case DELETE_SCHEDULE_ACTIVITY:
      return produce(state, (draftState) => {
        draftState.items = draftState.items.filter(
          (item) => item.id !== payload
        );
      });
    case DELETE_SCHEDULE_ACTIVITIES:
      return produce(state, (draftState) => {
        draftState.items = draftState.items.filter(
          (item) => !payload.includes(item.id)
        );
      });
    case GET_REGULARS_REQUEST:
      return {
        ...state,
        items: [],
        isFetchingRegulars: true,
        regulars: [],
        error: "",
      };
    case GET_REGULARS_SUCCESS:
      return produce(state, (draftState) => {
        draftState.regulars = payload;
        draftState.isFetchingRegulars = false;
        upsertEmbeddedSACustomColumns(draftState, payload);
      });
    case SET_REGULARS_SCOPE:
      // Delta-band scope switch: set the display window (+ coverage), re-derive the shown
      // rows from the pool locally. Narrowing a period lands ONLY here — zero network.
      return produce(state, (draftState) => {
        draftState.regularsWindow = payload.window || null;
        draftState.regularsCoverage = payload.coverage || null;
        if (payload.resetPool) draftState.regularsPool = [];
        draftState.regulars = draftState.regularsWindow
          ? draftState.regularsPool.filter((r) =>
              saRowInWindow(r, draftState.regularsWindow)
            )
          : draftState.regularsPool;
        // A scope switch settles the streaming state on the CURRENT window: a pure narrow
        // never dispatches a completing chunk, so without this the chip/counters from the
        // previous (wider) load stayed frozen on screen.
        draftState.regularsStreaming = false;
        draftState.regularsTotalRows = draftState.regulars.length;
        draftState.isFetchingRegulars = false;
        draftState.error = "";
      });
    case GET_REGULARS_CHUNK:
      return produce(state, (draftState) => {
        draftState.error = "";
        // Chunks feed the POOL (deduped by SA id); the displayed list is the pool filtered
        // by the selected window. Band chunks (widening) thus append beyond the old window
        // while carried-over rows stay untouched.
        const poolCountBefore = draftState.regularsPool.length;
        if (payload.replace) {
          draftState.regularsPool = payload.rows;
        } else if (payload.rows && payload.rows.length) {
          const seen = new Set(
            draftState.regularsPool.map((r) => r.scheduleActivity.id)
          );
          payload.rows.forEach((r) => {
            if (!seen.has(r.scheduleActivity.id)) {
              seen.add(r.scheduleActivity.id);
              draftState.regularsPool.push(r);
            }
          });
        }
        draftState.regulars = draftState.regularsWindow
          ? draftState.regularsPool.filter((r) =>
              saRowInWindow(r, draftState.regularsWindow)
            )
          : draftState.regularsPool;
        upsertEmbeddedSACustomColumns(draftState, payload.rows);
        // Clear the loading flag as soon as the first batch is in (not when the last chunk
        // lands), so the grid unmasks and shows rows progressively while the rest stream.
        // Stay "fetching" only while there is still nothing to show.
        draftState.isFetchingRegulars =
          !payload.complete && draftState.regulars.length === 0;
        // Streaming meta for the non-blocking "X / Y rows" chip. A band load's totalRows
        // only counts the band — offset by what was already pooled.
        draftState.regularsStreaming = !payload.complete;
        if (typeof payload.totalRows === "number") {
          draftState.regularsTotalRows = payload.band
            ? poolCountBefore + payload.totalRows
            : payload.totalRows;
        } else if (payload.complete) {
          draftState.regularsTotalRows = draftState.regulars.length;
        }
        if (payload.complete && payload.key) {
          draftState.regularsCache[payload.key] = draftState.regulars;
          draftState.regularsCacheKey = payload.key;
        }
      });
    case GET_REGULARS_FROM_CACHE:
      return produce(state, (draftState) => {
        draftState.regulars = draftState.regularsCache[payload.key] || [];
        draftState.regularsCacheKey = payload.key;
        draftState.isFetchingRegulars = false;
        draftState.error = "";
      });
    case GET_REGULARS_ERROR:
      return {
        ...state,
        isFetchingRegulars: false,
        error: payload,
      };
    case UPDATE_REGULAR:
      return {
        ...state,
        regulars: state.regulars.map((item) => {
          if (item.scheduleActivity.id === payload.scheduleActivity.id) {
            return payload;
          }
          return item;
        }),
      };
    case UPDATE_BATCH_OF_REGULARS: {
      // Pre-build Map for O(1) lookups instead of .find() O(n) per item
      const regularsPayloadMap = new Map(payload.map((sa) => [sa.id, sa]));
      return {
        ...state,
        regulars: state.regulars.map(item => {
          const relatedItem = regularsPayloadMap.get(item.scheduleActivity.id);
          // Immutable replace: state produced by the immer paths (chunk load) is frozen —
          // assigning item.scheduleActivity in place threw "Cannot assign to read only
          // property" and broke the multi-select batch delete.
          if (relatedItem) return { ...item, scheduleActivity: relatedItem };
          return item;
        })
      };
    }
    case UPDATE_SCHEDULE_ACTIVITIES_BY_IDS:
      return produce(state, (draftState) => {
        // Pre-build index map for O(1) lookups instead of .findIndex() O(n) per payload item
        const indexById = new Map();
        draftState.items.forEach((item, idx) => {
          indexById.set(item.id, idx);
          if (item?.scheduleActivity?.id) {
            indexById.set(item.scheduleActivity.id, idx);
          }
        });
        payload.forEach((item) => {
          const index = indexById.get(item.scheduleActivity.id);
          if (index == null) return;
          item.scheduleActivity.hasConflicts = item.hasConflicts;
          if (
            "globalCostAtCompletion" in item &&
            "laborCostAtCompletion" in item
          ) {
            item.scheduleActivity.globalCostAtCompletion =
              item.globalCostAtCompletion;
            item.scheduleActivity.laborCostAtCompletion =
              item.laborCostAtCompletion;
          }
          if ("plannedLaborCost" in item && "plannedGlobalCost" in item) {
            item.scheduleActivity.plannedLaborCost = item.plannedLaborCost;
            item.scheduleActivity.plannedGlobalCost = item.plannedGlobalCost;
          }
          if ("laborCostRemaining" in item && "globalCostRemaining" in item) {
            item.scheduleActivity.laborCostActuals = item.laborCostActuals;
            item.scheduleActivity.laborCostRemaining = item.laborCostRemaining;
            item.scheduleActivity.globalCostActuals = item.globalCostActuals;
            item.scheduleActivity.globalCostRemaining =
              item.globalCostRemaining;
          }
          // GET_SCHEDULE_ACTIVITIES_SUCCESS flattens the DTO extras (mappingTag,
          // spectrum, head-scope) onto the item; a by-ids refresh must not undo
          // that. Replacing the item with the bare scheduleActivity dropped the
          // tag from the store after every edit, so a re-render from state showed
          // the row untagged while the assignment was still persisted. An extra
          // the payload does not carry keeps its current value.
          const existing = draftState.items[index];
          const carry = (key) =>
            key in item ? item[key] ?? null : existing?.[key] ?? null;
          const headScopeNumber =
            "headScopeNumber" in item
              ? item.headScopeNumber ?? null
              : existing?.headScopeNumber ?? null;
          draftState.items[index] = {
            ...item.scheduleActivity,
            mappingTag: carry("mappingTag"),
            tagLevel: carry("tagLevel"),
            spectrum: carry("spectrum"),
            headScopeNumber,
            headScopeLabel: headScopeNumber > 0 ? `#${headScopeNumber}` : "",
          };
        });
      });
    case RESET_BASELINE_ON_ACTIVITY:
      return produce(state, (draftState) => {
        draftState.items = draftState.items.map((item) =>
          item.id == payload.id ? payload : item
        );
      });
    case RESET_BASELINE_ON_SUMMARY:
      return produce(state, (draftState) => {
        const saMap = new Map(payload.map((sa) => [sa.id, sa]));
        draftState.items = draftState.items.map((item) => {
          const updated = saMap.get(item.id);
          return updated || item;
        });
      });
    case ADD_SUBTASK_SUCCESS:
      return produce(state, (draftState) => {
        const { child, parent } = payload;
        const itemsWithParentUpdated = draftState.items.map((item) =>
          item.id === parent.id ? parent : item
        );
        draftState.items = [...itemsWithParentUpdated, child];
      });
    case ADD_SUMMARY_SUCCESS:
      const { parent, addedRegulars, summary } = payload;
      const addedTasks = [...addedRegulars, summary];
      return produce(state, (draftState) => {
        const itemsWithParentUpdated = draftState.items.map((item) =>
          item.id === parent.id ? parent : item
        );
        draftState.items = [...itemsWithParentUpdated, ...addedTasks];
      });
    case APPLY_CONFORM_PROGRESS:
      return produce(state, (draftState) => {
        draftState.items = draftState.items.map((item) => {
          if (payload.progressDTOs.includes(item.id)) {
            const progressPercentage = calculateProgressPercentage(
              item.earlyStartDate,
              item.earlyFinishDate,
              payload.dateOfReview
            );
            item.progress = progressPercentage;
          }
          return item;
        });
      });
    case GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_REQUEST:
      return produce(state, (draftState) => {
        draftState.isFetchingColumns = true;
        draftState.error = "";
      });
    case GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isFetchingColumns = false;
        draftState.customColumns = payload || [];
      });
    case UPDATE_SCHEDULE_ACTIVITY_CUSTOM_COLUMN:
      return produce(state, (draftState) => {
        draftState.customColumns = draftState.customColumns.map((column) =>
          column.id === payload.scheduleActivityId
            ? { ...column, ...payload.data }
            : column
        );
      });
    case UPDATE_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_VALUE:
      return produce(state, (draftState) => {
        let updated = false;
        const newCustomColumns = draftState.customColumns.map((column) => {
          if (column.id === payload.id) {
            updated = true;
            return {
              ...column,
              [payload.columnKey]: payload.value,
            };
          } else return column;
        });
        if (!updated) {
          newCustomColumns.push({
            id: payload.id,
            [payload.columnKey]: payload.value,
          });
        }
        draftState.customColumns = newCustomColumns;
      });
    case GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_ERROR:
      return produce(state, (draftState) => {
        draftState.isFetchingColumns = false;
        draftState.error = payload;
      });
    case GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_NAMES_REQUEST:
      return produce(state, (draftState) => {
        draftState.isFetchingColumnNames = true;
        draftState.error = "";
      });
    case GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_NAMES_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isFetchingColumnNames = false;
        draftState.customColumnNames = Array.isArray(payload) ? payload : [];
      });
    case GET_RESOURCE_TASKS_CUSTOM_COLUMN_NAMES_ERROR:
      return produce(state, (draftState) => {
        draftState.isFetchingColumnNames = false;
        draftState.error = payload;
      });
    case CLEAR_SCHEDULE_ACTIVITIES:
      return {
        ...state,
        items: [],
        tasks: [],
      };
    case UPDATE_SCHEDULE_ACTIVITY_FINISH_DATE_WITH_DURATION_SUCCESS:
      return {
        ...state,
        regulars: state.regulars.map((item) => {
          if (item.scheduleActivity.id === payload.id) {
            return {
              ...item,
              scheduleActivity: payload,
            };
          }
          return item;
        }),
      };
    case SHOW_MASK_ON_GANTT:
      return produce(state, (draftState) => {
        draftState.showMaskOnGantt = true;
      });
    case UPDATE_FINISH_AND_START:
      return produce(state, (draftState) => {
        draftState.regulars = draftState.regulars.map((item) => {
          if (item.scheduleActivity.id === payload.id) {
            item.scheduleActivity.startDate = payload.startDate;
            item.scheduleActivity.finishDate = payload.finishDate;
          }
          return item;
        });
      });
    case PROPAGATE_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_VALUE:
      return produce(state, (draftState) => {
        const nonExistingIds = payload.ids.filter((idToUpdate) =>
          draftState.customColumns.every(({ id }) => id != idToUpdate)
        );
        const updatedCustomColumns = draftState.customColumns.slice();
        // condition to avoid going through the array (which is sometimes big) when not necessary
        if (nonExistingIds.length !== payload.ids.length) {
          updatedCustomColumns.forEach((customColumn) => {
            const shouldPropagate = payload.ids.some(
              (id) => id == customColumn.id
            );
            if (shouldPropagate)
              customColumn[payload.customColumnField] = payload.value;
          });
        }
        nonExistingIds.forEach((id) => {
          const customColumn = { id };
          customColumn[payload.customColumnField] = payload.value;
          updatedCustomColumns.push(customColumn);
        });
        draftState.customColumns = updatedCustomColumns;
      });
    default:
      return state;
  }
}

// eslint-disable-next-line
export default function scheduleActivityReducer(state = initialState, action = {}) {
  const next = baseScheduleActivityReducer(state, action);
  if (
    next !== state &&
    REGULARS_CACHE_INVALIDATING_TYPES.has(action.type) &&
    next.regularsCache &&
    Object.keys(next.regularsCache).length > 0
  ) {
    // Also drop the delta-band coverage and resync the pool to the DISPLAYED rows: the
    // mutation reducers only touch `regulars`, so the pool would otherwise go stale. With
    // coverage null the next period change is a full reload (correctness over reuse).
    return {
      ...next,
      regularsCache: {},
      regularsCacheKey: null,
      regularsCoverage: null,
      regularsPool: next.regulars,
    };
  }
  return next;
}
